import useManufacturerShareImage from "@/hooks/useManufacturerShareImage";
import { useEffect } from "react";
import { ALL_CREDENTIALS, BUSINESS_DESCRIPTION } from "@/data/credentials";
import { REVIEW_STATS, formatRating } from "@/data/reviewStats";
import { BUSINESS_LOGO, withBusinessLogos } from "@/data/businessLogo";

import { DEFAULT_SHARE_IMAGE } from "@/data/cityProjectImages";

const BASE_URL = "https://1woodfloors.com";

interface PageSEOOptions {
  title: string;
  description: string;
  path: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  image?: { url: string; width: number; height: number; alt: string };
}

const usePageSEO = ({ title, description, path, type = "website", jsonLd, noindex = false, image }: PageSEOOptions) => {
  const manufacturerImage = useManufacturerShareImage(path);
  image = image ?? manufacturerImage;
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set or create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard meta
    setMeta("name", "description", description);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", `${BASE_URL}${path}`);

    // Twitter
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // City share images restore previous page-specific values on navigation.
    const imageTags: [string, string, string][] = image ? [
      ["property", "og:image", image.url], ["name", "twitter:image", image.url],
      ["property", "og:image:width", String(image.width)], ["property", "og:image:height", String(image.height)],
      ["property", "og:image:alt", image.alt],
    ] : [];
    const defaultImageValues: Record<string, string> = {
      "og:image": DEFAULT_SHARE_IMAGE.url, "twitter:image": DEFAULT_SHARE_IMAGE.url,
      "og:image:width": String(DEFAULT_SHARE_IMAGE.width), "og:image:height": String(DEFAULT_SHARE_IMAGE.height),
      "og:image:alt": DEFAULT_SHARE_IMAGE.alt,
    };
    const previousImages = imageTags.map(([, key]) => defaultImageValues[key]);
    imageTags.forEach(([attr, key, content]) => setMeta(attr, key, content));

    // Robots
    if (noindex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      setMeta("name", "robots", "index, follow");
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE_URL}${path}`);

    // JSON-LD — skip if static version already baked into HTML by build script
    const hasStaticJsonLd = document.querySelector('script[type="application/ld+json"]:not(#page-jsonld)') !== null;
    let scriptEl = document.getElementById("page-jsonld") as HTMLScriptElement | null;
    if (jsonLd && !hasStaticJsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = "page-jsonld";
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(withBusinessLogos(jsonLd));
    }

    return () => {
      imageTags.forEach(([attr, key], i) => {
        const el = document.querySelector(`meta[${attr}="${key}"]`);
        const previous = previousImages[i];
        if (previous !== null) el?.setAttribute("content", previous);
        else el?.remove();
      });
      document.title = "1 Wood Floors";
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, path, type, jsonLd, noindex, image]);
};

// Reusable LocalBusiness schema
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "logo": BUSINESS_LOGO,
  "@id": "https://1woodfloors.com/#localbusiness",
  "name": "1 Wood Floors",
  "alternateName": "First Class Floor Covering, INC",
  "url": "https://1woodfloors.com",
  "telephone": "+12483258853",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "33475 Seven Mile Rd",
    "addressLocality": "Livonia",
    "addressRegion": "MI",
    "postalCode": "48152",
    "addressCountry": "US",
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 42.425722,
    "longitude": -83.376634,
  },
  "hasMap": "https://www.google.com/maps/dir/?api=1&destination=1+Wood+Floors+33475+W+7+Mile+Rd+Livonia+MI+48152",
  "image": DEFAULT_SHARE_IMAGE.url,
  "description": BUSINESS_DESCRIPTION,
  "knowsAbout": [
    "Hardwood Floor Installation",
    "Hardwood Floor Refinishing",
    "Subfloor Leveling and Repair",
    "Luxury Vinyl Plank Installation",
    "Laminate Floor Installation",
    "NWFA Certified Inspection",
    "Full-Service Flooring Project Management",
    "Residential General Contracting",
  ],
  "priceRange": "$$",
  "openingHours": ["Mo 10:00-14:30", "Tu-Fr 10:00-17:00"],
  "hasCredential": [...ALL_CREDENTIALS],
  "award": "2017 NWFA Wood Floor of the Year",
  "areaServed": [
    "Livonia", "Northville", "Novi", "Plymouth", "Ann Arbor",
    "Canton", "Farmington Hills", "West Bloomfield", "Birmingham",
    "Bloomfield Hills", "Troy", "Rochester Hills", "Dearborn",
    "Grosse Pointe", "Brighton", "Saline", "Harbor Springs",
  ].map(city => ({ "@type": "City", "name": `${city}, MI` })),
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Hardwood Flooring Services",
    "itemListElement": [
      { "@type": "Service", "name": "Hardwood Floor Installation", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Dust Control Refinishing", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "NWFA Certified Inspections", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Subfloor Leveling & Repair", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Luxury Vinyl Plank Installation", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Waterproof Laminate Installation", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Alterna Engineered Tile Installation", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
      { "@type": "Service", "name": "Commercial Flooring", "provider": { "@id": "https://1woodfloors.com/#localbusiness" } },
    ],
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": formatRating(),
    "reviewCount": String(REVIEW_STATS.count),
    "bestRating": "5",
  },
};

export const makeServiceSchema = (name: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "provider": {
    "@type": "LocalBusiness",
    "name": "1 Wood Floors",
    "telephone": "+1-248-325-8853",
    "address": { "@type": "PostalAddress", "addressLocality": "Livonia", "addressRegion": "MI" },
  },
  "areaServed": { "@type": "State", "name": "Michigan" },
  "description": description,
});

export const makeCityServiceSchema = (cityName: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": `Hardwood Flooring in ${cityName}, MI`,
  "provider": {
    "@type": "LocalBusiness",
    "name": "1 Wood Floors",
    "telephone": "+1-248-325-8853",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "33475 Seven Mile Road West",
      "addressLocality": "Livonia",
      "addressRegion": "MI",
      "postalCode": "48152",
      "addressCountry": "US",
    },
    "url": "https://1woodfloors.com",
  },
  "areaServed": {
    "@type": "City",
    "name": `${cityName}, MI`,
  },
  "description": description,
});

export const makeArticleSchema = (headline: string, description: string, datePublished?: string) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": headline,
  "description": description,
  "author": { "@type": "Organization", "name": "1 Wood Floors", "url": "https://1woodfloors.com", "logo": BUSINESS_LOGO },
  "publisher": { "@type": "Organization", "name": "1 Wood Floors", "logo": BUSINESS_LOGO },
  ...(datePublished && { datePublished }),
  "mainEntityOfPage": { "@type": "WebPage" },
});

export default usePageSEO;
